import { useMemo } from 'react'
import { IntegrationServiceId, isBusinessEventExportEnabledForService } from '../../../../common/integrations'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../store/hooks'
import { useGetMeQuery } from '../../../../store/user/user'
import { IntegrationLoginURLSetting } from '../IntegrationSpecificSettings/IntegrationLoginURLSetting'
import { ContactAdminSettings } from './AdminSettingsTabs/InboundMappingSettings/ContactMappingSettings/ContactAdminSettings'
import { SessionAdminSettings } from './AdminSettingsTabs/OutboundMappingSettings/SessionMappingSettings/SessionAdminSettings'
import { TaskAdminSettings } from './AdminSettingsTabs/OutboundMappingSettings/TaskMappingSettings/TaskAdminSettings'
import { AdditionalObjectsAdminSettings } from './AdminSettingsTabs/InboundMappingSettings/AdditionalObjectsMappingSetting/AdditionalObjectsAdminSettings'
import { useZocksQuota } from '../../../../components/ScopedBlock/hooks'
import { BusinessEventsMappingSettings } from './AdminSettingsTabs/OutboundMappingSettings/BusinessEventsMappingSettings/BusinessEventsMappingSettings'
import { useAccount } from '../../../../common/useAccount'

export const useCustomizableAdminSettings = (serviceId: IntegrationServiceId) => {
  const accountId = useAppSelector(selectAccountId)
  const { account } = useAccount({ refetchOnMountOrArgChange: true })

  const { data: userData } = useGetMeQuery({ accountId }, { refetchOnMountOrArgChange: true })

  const hasIntegration = useMemo(() => {
    if (!userData || !userData.integrations) {
      return false
    }
    return !!userData.integrations.some((i) => i.serviceId === serviceId)
  }, [userData, serviceId])

  const hasBusinessEventExportAccess = useMemo(() => account?.effectiveConfig.businessEventExport ?? false, [account])

  const { hasQuota } = useZocksQuota()

  const hasCreateMappingQuota = hasQuota('integration.createMappingEnabled.value')
  const disableTab = useMemo(() => !hasIntegration || !hasCreateMappingQuota, [hasIntegration, hasCreateMappingQuota])

  return useMemo(() => {
    const adminSettingsTabs = [
      { id: 'url', label: 'URL Settings', content: () => <IntegrationLoginURLSetting serviceId={serviceId} /> },
      { id: 'contact', label: 'Contact', content: () => <ContactAdminSettings serviceId={serviceId} />, disabled: !hasIntegration },
      { id: 'meetingSummary', label: 'Meeting Summary', content: () => <SessionAdminSettings serviceId={serviceId} />, disabled: !hasIntegration },
      { id: 'task', label: 'Task', content: () => <TaskAdminSettings serviceId={serviceId} />, disabled: disableTab },
      {
        id: 'additionalObjects',
        label: 'AI Context Objects',
        content: () => <AdditionalObjectsAdminSettings serviceId={serviceId} />,
        disabled: disableTab,
      },
    ]
    if (isBusinessEventExportEnabledForService(serviceId)) {
      adminSettingsTabs.push({
        id: 'businessEvents',
        label: 'Client signals',
        content: () => <BusinessEventsMappingSettings serviceId={serviceId} />,
        disabled: disableTab || !hasBusinessEventExportAccess,
      })
    }
    return adminSettingsTabs
  }, [serviceId, disableTab, hasIntegration, hasBusinessEventExportAccess])
}
